<template>
  <LoadingGate
    v-if="featureFlagEnabled"
    :active="!hasError"
    :statusText="statusText"
    opaque
    fullscreen
  >
  <ErrorState :title="locale.errorTitle">
    <div class="flex flex-col justify-center">
      <div class="text-lg font-bold mb-base">
        {{ errorText }}
      </div>
      <ButtonLink
        :to="{ name: 'Integrations' }">
        {{ locale.errorBackLink }}
      </ButtonLink>
    </div>
  </ErrorState>
  </LoadingGate>
</template>

<script>
import { defineComponent, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { useAuthorise, useFeatureFlag } from '@/composables/integrations/slack';
import { delaySeconds } from '@/utils/delay';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import { LoadingGate } from '@/components/gates';
import { ErrorState } from '@/components/ui/common/states';
import { t } from '@/locales';
import { notifyError } from '@/utils/notify';

export default defineComponent({
  name: 'AuthorisePage',
  components: {
    LoadingGate,
    ErrorState,
    ButtonLink,
  },
  setup() {
    const locale = {
      errorTitle: t('integrations.slack.pages.authorise.errorState.title'),
      errorBackLink: t('integrations.slack.pages.authorise.errorState.backLink'),
      processing: t('integrations.slack.pages.authorise.processing'),
      denied_request: t('integrations.slack.pages.authorise.denied_request'),
      invalid_query: t('integrations.slack.pages.authorise.invalid_query'),
      invalid_csrf: t('integrations.slack.pages.authorise.invalid_csrf'),
      authorising: t('integrations.slack.pages.authorise.authorising'),
      success: t('integrations.slack.pages.authorise.success'),
    };

    const router = useRouter();
    const featureFlag = useFeatureFlag();

    const {
        isQueryValid,
        wasAccessDeniedByUser,
        isCsrfValid,
        authorise,
    } = useAuthorise();

    const hasError = ref(false);
    const statusText = ref(locale.processing);
    const errorText = ref('');

    function setStatus(text) {
      statusText.value = text;
    }

    function setError(text) {
      errorText.value = text;
      hasError.value = true;
    }

    async function auth() {
      try {
        if (wasAccessDeniedByUser()) {
          return setError(locale.denied_request);
        }
        if (!isQueryValid()) {
          return setError(locale.invalid_query);
        }
        if (!isCsrfValid()) {
          return setError(locale.invalid_csrf);
        }

        await delaySeconds(1);

        setStatus(locale.authorising);

        await authorise();

        setStatus(locale.success);

        await delaySeconds(2);

        return router.push({ name: 'Integrations' });
      } catch (err) {
        notifyError('integrations.slack.pages.authorise.unknown_error');

        return router.push({ name: 'Integrations' });
      }
    }

    auth();

    return {
      locale,
      featureFlagEnabled: featureFlag.isEnabled,
      hasError,
      statusText,
      errorText,
    };
  },
});
</script>
